import {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useContext,
  SetStateAction,
  Dispatch,
} from 'react';
import { find } from 'lodash';
import { HttpError } from '@wix/http-client';
import { useExperiments } from '@wix/wix-experiments-react';
import type { PluginPlacement } from '@wix/ambassador-devcenter-appsruntimereader-v1-app-runtime-data/types';

import { WidgetPlugin, WidgetSlot } from '../../../types/widgetPlugins';
import { WidgetDataContext } from '../widgetDataContext';

const placementHash = (placement: PluginPlacement) => {
  return `${placement.widgetId}-${placement.slotId}-${placement.appDefinitionId}`;
};

function findWidgetSlotByPlacements(
  widgetSlots: WidgetSlot[],
  widgetPlugin: WidgetPlugin,
) {
  const existingSlot = widgetSlots.find(
    (slot) => slot?.pluginInfo?.widgetId === widgetPlugin.widgetRefId,
  );

  if (existingSlot) {
    return existingSlot;
  }

  const widgetSlotsByPlacement = widgetSlots.reduce((acc, slot) => {
    acc[placementHash(slot.placement)] = slot;
    return acc;
  }, {} as Record<string, WidgetSlot>);

  let topPrioritySlot: WidgetSlot | undefined;

  for (const placement of widgetPlugin.placements) {
    const slot = widgetSlotsByPlacement[placementHash(placement)];

    if (slot && !topPrioritySlot) {
      topPrioritySlot = slot;
    }

    if (slot && !slot.pluginInfo) {
      return slot;
    }
  }

  return topPrioritySlot;
}

function getWidgetSlotsPlacements(
  widgetSlots: WidgetSlot[],
): PluginPlacement[] {
  return Array.from(new Set(widgetSlots.map((slot) => slot.placement)));
}

function getIncludeAppsList(widgetSlots: WidgetSlot[]) {
  const includeApps = widgetSlots
    .map((slot) => slot.pluginInfo?.appDefinitionId)
    .filter(Boolean) as string[];

  const appsToIncludeQueryString = new URL(
    window.location.href,
  ).searchParams.get('include-widget-plugin-apps');

  if (appsToIncludeQueryString) {
    includeApps.push(...appsToIncludeQueryString.split(','));
  }
  return includeApps;
}

export type PanelError = HttpError | Error;

export interface IWidgetsData {
  plugins: WidgetPlugin[];
  isLoading: boolean;
  error: PanelError | undefined;
  fetchWidgetPlugins: () => void;
  resetPluginInstallationStateBySlot: (slot: WidgetSlot) => void;
  setPanelError: Dispatch<SetStateAction<PanelError | undefined>>;
}

export const useWidgetPlugins = (): IWidgetsData => {
  const [plugins, setPlugins] = useState<WidgetPlugin[]>([]);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [error, setPanelError] = useState<PanelError | undefined>();
  const { editorIntegrationAPI, widgetSlots, fedopsLogger } =
    useContext(WidgetDataContext);
  const fetchWidgetPlugins = useCallback(async () => {
    fedopsLogger.interactionStarted('plugins-loading-interaction');
    setLoading(true);
    const slotIds = widgetSlots.map((s) => s.id).filter(Boolean);
    const params = {
      includeApps: getIncludeAppsList(widgetSlots),
      placements: getWidgetSlotsPlacements(widgetSlots),
      slotIds,
    };
    try {
      const pluginsResponse =
        await editorIntegrationAPI.getWidgetPluginsRequest(params);
      const widgetPlugins = pluginsResponse.map((plugin) => ({
        ...plugin,
        slot: findWidgetSlotByPlacements(widgetSlots, plugin),
      }));

      setPlugins(widgetPlugins);
      setPanelError(undefined);
      fedopsLogger.interactionEnded('plugins-loading-interaction');
    } catch (err) {
      setPlugins([]);
      setPanelError(err as HttpError);
    } finally {
      setLoading(false);
    }
  }, [widgetSlots, fedopsLogger]);

  useEffect(() => {
    void fetchWidgetPlugins();
  }, [fetchWidgetPlugins]);

  const resetPluginInstallationStateBySlot = useCallback(
    (slot: WidgetSlot) => {
      const updatedPlugins = plugins.map((plugin) =>
        plugin?.slot?.compRef.id === slot.compRef.id
          ? { ...plugin, slot: { ...plugin.slot, pluginInfo: undefined } }
          : plugin,
      );

      setPlugins(updatedPlugins);
    },
    [plugins],
  );

  // useMemo in this case allows to provide the whole object as-is
  return useMemo(
    () => ({
      plugins,
      isLoading,
      error,
      fetchWidgetPlugins,
      resetPluginInstallationStateBySlot,
      setPanelError,
    }),
    [
      plugins,
      isLoading,
      error,
      fetchWidgetPlugins,
      resetPluginInstallationStateBySlot,
      setPanelError,
    ],
  );
};
